/**
 * a group of agent-related selectors that involve array / object manipulation
 */

import { createSelector } from 'reselect';
import { AgentRow, Store } from '../types';
import { AgentMap } from '../reducers/risk';

const selectAgentMap = (store: Store): AgentMap => store.risk.agents.agentMap;

/**
 * count all agent rows
 */
const selectNumberOfAgents = createSelector(
  [selectAgentMap],
  (agentMap) => Object.values(agentMap).flat().length,
);

/**
 * count how many agent rows are online
 */
const selectNumberOfOnlineAgents = createSelector(
  [selectAgentMap],
  (agentMap) =>
    Object.values(agentMap)
      .flat()
      .filter((agent) => agent.status === 'Online').length,
);

/**
 * count how many agent rows are currently contained
 */
const selectNumberOfContainedAgents = createSelector(
  [selectAgentMap],
  (agentMap) =>
    Object.values(agentMap)
      .flat()
      .filter((agent) => agent.contained === 'Contained').length,
);

/**
 * flatten the rows of agentMap's values and filter out those in progress
 * this data is used during the export and during progress calculation
 */
const selectCompleteAgentRows = createSelector([selectAgentMap], (agentMap) =>
  Object.values(agentMap)
    .flat()
    .filter((agent) => !agent.queryInProgress),
);

/**
 * Due to upstream API implementation, agents reported >90 days ago would have
 *   an empty health check time, as well as other null fields
 * Having blank rows do more harm than good. Hide them by doing a truthy check.
 */
const selectAgentRowsForTable = createSelector(
  [selectCompleteAgentRows],
  (agentRows) => agentRows.filter((agentRow) => agentRow.heartbeatTime),
);

const agentHasBlanks = (agentRow: AgentRow) =>
  !(
    agentRow.hostname &&
    agentRow.userName &&
    agentRow.ipAddress &&
    agentRow.OSVersion
  );

/**
 * Check if any of the rows contain blanks in the table
 */
const selectTableHasBlanks = createSelector(
  [selectCompleteAgentRows],
  (agentRows) => agentRows.some((agentRow) => agentHasBlanks(agentRow)),
);

/**
 * select the IDs of in progress agents
 * Note: the UI should not consume any other fields of these agents
 */
const selectIncompleteAgentIDs = createSelector([selectAgentMap], (agentMap) =>
  Object.values(agentMap).reduce<string[]>(
    (ids, agentRows) =>
      agentRows[0].queryInProgress ? [...ids, agentRows[0].id] : ids,
    [],
  ),
);

export const memoizedAgentSelectors = {
  selectNumberOfAgents,
  selectNumberOfOnlineAgents,
  selectNumberOfContainedAgents,
  selectCompleteAgentRows,
  selectAgentRowsForTable,
  selectTableHasBlanks,
  selectIncompleteAgentIDs,
};
